import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const roomBookingApi = createApi({
  reducerPath: "roomBookingApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api",
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("uid");
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Profile"],
  endpoints: (builder) => ({
    registerUserAndBookRoom: builder.mutation({
      query: (body) => ({
        url: "/register-user",
        method: "POST",
        body,
      }),
    }),
    registerUser: builder.mutation({
      query: (body) => ({
        url: "/public/users",
        method: "POST",
        body,
      }),
    }),
    loginUser: builder.mutation({
      query: (body) => ({
        url: "/public/auth/login",
        method: "POST",
        body,
      }),
    }),
    getUserProfile: builder.query({
      query: () => "/public/profile",
      providesTags: ["Profile"],
    }),
    updateUserPassword: builder.mutation({
      query: (body) => ({
        url: "/public/profile/password",
        method: "PUT",
        body,
      }),
    }),
    createPaymentOrder: builder.mutation({
      query: (body) => ({
        url: "/payment/create-order",
        method: "POST",
        body,
      }),
    }),
    verifyPayment: builder.mutation({
      query: (body) => ({
        url: "/payment/verify-payment",
        method: "POST",
        body,
      }),
    }),
    sendOtp: builder.mutation({
      query: (body) => ({
        url: "/public/auth/send-otp",
        method: "POST",
        body,
      }),
    }),
    verifyOtp: builder.mutation({
      query: (body) => ({
        url: "/public/auth/verify-otp",
        method: "POST",
        body,
      }),
    }),
    getMyPayments: builder.query({
      query: () => "/payment/my-payments",
      providesTags: ["Profile"],
    }),
    getPaymentsByMobile: builder.mutation({ // Using mutation as we pass mobile in body safely
      query: (body) => ({
        url: "/payment/my-payments-mobile",
        method: "POST",
        body
      }),
    }),
    getAllPayments: builder.query({
      query: () => "/payment/all-payments",
    }),
    getPaymentReports: builder.query({
      query: (params) => ({
        url: "/payment/reports",
        params, // startDate, endDate, category, mode
      }),
    }),
    getOrderDetails: builder.query({
      query: (id) => `/payment/order/${id}`,
      providesTags: ["Profile"],
    }),
    getBookings: builder.query({
      query: () => "/room_bookings",
      providesTags: ["Bookings"],
    }),
    updateBookingStatus: builder.mutation({
      query: ({ id, status, room_no, room_nos }) => ({ // Added room_nos support
        url: `/room_bookings/${id}`,
        method: "PUT",
        body: { status, room_no, room_nos },
      }),
      invalidatesTags: ["Bookings"],
    }),
    getAvailableRooms: builder.mutation({
      query: (buildingId) => `/available-rooms/${buildingId}`,
    }),
    // Settings Endpoints
    getSettings: builder.query({
      query: () => "/settings",
      providesTags: ["Settings"],
    }),
    updateSettings: builder.mutation({
      query: (body) => ({
        url: "/settings",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Settings"],
    }),
  }),
});

export const {
  useRegisterUserAndBookRoomMutation,
  useRegisterUserMutation,
  useLoginUserMutation,
  useGetUserProfileQuery,
  useUpdateUserPasswordMutation,
  useCreatePaymentOrderMutation,
  useVerifyPaymentMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useGetMyPaymentsQuery,
  useGetPaymentsByMobileMutation,
  useGetAllPaymentsQuery,
  useGetPaymentReportsQuery,
  useGetOrderDetailsQuery,
  useGetBookingsQuery,
  useUpdateBookingStatusMutation,
  useGetAvailableRoomsMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation
} = roomBookingApi;
